import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import { reorderWorkspaces } from '../../../../lib/workspaces';
import type { WorkspaceRowModel } from './use-workspace-rows';

/**
 * Reordering the spaces list by dragging it, in place, in the menu.
 *
 * There is no separate surface for this — no dialog, no mode, no hidden gesture. Each row carries a grip
 * at its inline-start; drag the grip and the list rearranges under it, drop and it persists. Both menu
 * variants render the same rows (`useWorkspaceRows`), so both get the behaviour from here rather than
 * each rolling its own.
 *
 * **The grip is the only drag target, and that is what makes this work on touch.** `touch-action: none`
 * lives on the grip alone (see workspace-drag.module.css), so a finger on the grip drags the row while a
 * finger anywhere else still scrolls the menu — which is the conflict that would otherwise make dragging
 * inside a scrolling drawer unusable.
 *
 * **A space cannot leave the spaces list.** Both the order and the row's own travel are confined to the
 * workspace rows: a swap can only land inside them, and `clampLift` stops the row half a slot short of
 * its neighbour and dead at the first and last slot. There is nowhere else in the menu to drop a space.
 *
 * **One space is not a list.** With a single row there is nothing to reorder against, so `canReorder` is
 * false, no grip renders and no gesture starts. A handle that cannot move anything reads as a broken
 * control, and the gate lives here rather than at each call site so the two menus cannot disagree.
 *
 * **The commit is the drop.** There is nothing to confirm: `reorderWorkspaces` patches the shared React
 * Query cache first (so the rail, the drawer and the home strip all move at once), then persists, then
 * rolls itself back and re-reads if the server refuses. So this hook holds a local arrangement only for
 * the length of the gesture and hands it over on release.
 *
 * **A swap must be invisible on the dragged row.** Crossing the half-row threshold moves the row a whole
 * slot in the DOM and re-anchors the gesture by the same step, so the two cancel and the row stays exactly
 * under the finger. That only holds if the clamp is applied to the slot the row has ARRIVED in: clamping
 * against the slot it just left collapses the limit to zero at either end of the list (`clampLift` gives a
 * first row no upward travel and a last row no downward travel), which snapped the row half a row away
 * from the pointer for a frame and then snapped it back. Both the swap and the clamp therefore read the
 * live arrangement out of `orderRef`, not out of `order` — a second pointermove in the same frame sees a
 * state variable that has not re-rendered yet, and would compute the index it already used.
 *
 * **The row that gives up the slot slides, it does not teleport.** Nothing else in the list is
 * transformed, so a swap moves the neighbour a whole row instantly; that jump is the other half of what
 * reads as "jumpy". `SLIDE_MS` plays the displacement backwards on that one row (a FLIP, through the Web
 * Animations API so it never fights React's inline `transform` on the dragged row) — the dragged row
 * stays welded to the pointer with no easing, and only the list under it animates.
 *
 * **The gesture is followed on `window`, not on the grip.** A downward swap makes React move the DRAGGED
 * row's DOM node (it re-inserts the row whose old index is behind the last one it placed), and moving a
 * node releases its pointer capture. The grip then stopped hearing `pointermove` and `pointerup`: the row
 * froze half a row down, the grip faded out, and the row stayed lifted after the release. Upward swaps
 * move the neighbour instead, which is why only dragging down stuck. Listening on `window` from
 * pointerdown to pointerup does not depend on capture at all.
 */

/** Marks a row element so the drag can measure the real distance between two of them. */
export const WORKSPACE_ROW_ATTR = 'data-workspace-row';

/**
 * How long the displaced row takes to slide into the slot the dragged row vacated. Short enough that a
 * fast drag across several rows never queues up visible motion, long enough to read as a slide.
 */
export const SLIDE_MS = 160;

/** `useLayoutEffect` warns when React renders on the server; the slide only exists in a browser anyway. */
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export interface WorkspaceDragModel {
  /** The rows to render — the live list, or the arrangement currently being dragged. */
  rows: WorkspaceRowModel[];
  /** False when there is nothing to reorder (a single space): render no grip, start no drag. */
  canReorder: boolean;
  /** True while this row is the one being dragged (the call site paints it as lifted). */
  isDragging: (id: string) => boolean;
  /** Follow-the-pointer transform for the dragged row; undefined for every other row. */
  liftStyle: (id: string) => CSSProperties | undefined;
  /** Spread on the row itself. */
  rowProps: () => { [WORKSPACE_ROW_ATTR]: string; draggable: false; onClickCapture: (event: { preventDefault: () => void; stopPropagation: () => void }) => void };
  /** Spread on the grip. */
  gripProps: (id: string) => {
    onPointerDown: (event: PointerEvent<HTMLElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  };
}

/**
 * The real distance from one row to the next, measured off the DOM rather than assumed. A constant would
 * be a second copy of the CSS: the rail and the drawer have different row heights and gaps, and a spacing
 * tweak would silently desync the drag from what the eye sees.
 */
function measureStep(row: HTMLElement | null): number {
  if (!row) return 0;
  const rect = row.getBoundingClientRect();
  const siblings = Array.from(row.parentElement?.querySelectorAll<HTMLElement>(`[${WORKSPACE_ROW_ATTR}]`) ?? []);
  const index = siblings.indexOf(row);
  const neighbour = siblings[index + 1] ?? siblings[index - 1];
  if (!neighbour) return rect.height;
  return Math.abs(neighbour.getBoundingClientRect().top - rect.top) || rect.height;
}

/**
 * How far the dragged row may follow the pointer, in px, from the slot it currently occupies.
 *
 * **This is what keeps a space inside the spaces list.** The ORDER was already confined — a swap can only
 * land inside the workspace rows — but the row's `translateY` was not, so holding the grip and dragging up
 * carried the row visually out of the section and over Chat, Home and the section header. It looked like a
 * space could be dropped into another part of the menu, and nothing about the section's edge said otherwise.
 *
 * Half a row in either direction is all the travel a swap ever needs (past that, the swap fires and the
 * anchor moves with it), so that is the limit — and at the two ends there is no neighbour to swap with, so
 * the travel in the outward direction is zero. The row stops dead at the first and last slot.
 */
/**
 * The slot the dragged row occupies after this pointer position is taken into account — its current one,
 * or the neighbour it has just crossed half a row into.
 *
 * It is split out because BOTH the swap and the clamp have to be answered with the same slot. They used
 * to be answered with different ones: the swap moved the row on, and `clampLift` was then handed the slot
 * it had just LEFT. At either end of the list that collapses the limit to zero (`clampLift` gives a first
 * row no upward travel and a last row no downward travel), so the row was pinned to its new slot's origin
 * for one frame — half a row away from the finger — and the next pointermove, reading a fresh index, put
 * it straight back. Measured off a screen recording: a 22px jump and a 21px snap back on consecutive
 * frames, at exactly half the 43.75px row step.
 */
export function nextSlot(index: number, delta: number, step: number, count: number): number {
  const limit = step / 2;
  if (delta > limit && index < count - 1) return index + 1;
  if (delta < -limit && index > 0) return index - 1;
  return index;
}

export function clampLift(raw: number, step: number, index: number, count: number): number {
  const limit = step / 2;
  const down = index >= count - 1 ? 0 : limit;   // positive = toward the end of the list
  const up = index <= 0 ? 0 : -limit;
  return Math.min(down, Math.max(up, raw));
}

/**
 * `shown` is how many rows the call site actually renders, when it folds the rest away (the new rail shows
 * five, then "More spaces"). A swap past it would move the dragged row into a row that is not on screen, so
 * the row would unmount under the pointer; the drag and the arrow keys stay inside the rendered rows.
 */
export function useWorkspaceDrag(rows: WorkspaceRowModel[], shown: number = Infinity): WorkspaceDragModel {
  // Two rows is the smallest list that can be rearranged. Below that the whole affordance is off.
  const canReorder = rows.length > 1;
  // The arrangement being dragged, as ids. `null` = follow the server list.
  const [order, setOrder] = useState<string[] | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [lift, setLift] = useState(0);
  // Where the current step started; moves WITH the row, so each further half-row triggers the next swap.
  const origin = useRef(0);
  const step = useRef(0);
  // Whether this gesture actually reordered anything — decides both whether to persist and whether the
  // click that follows the release has to be swallowed (the rows are links).
  const moved = useRef(false);
  // The same values as `order`/`draggingId`, readable SYNCHRONOUSLY. A pointermove is a continuous event,
  // so two of them can land before React re-renders; reading the state there would compute the swap and
  // the clamp against an arrangement one step out of date.
  const orderRef = useRef<string[] | null>(null);
  const draggingRef = useRef<string | null>(null);
  // The container the rows share, captured when the gesture starts — the slide needs to find them again.
  const listRef = useRef<HTMLElement | null>(null);
  // The row displaced by the last swap, and how far it travelled, waiting for the DOM to hold the new order.
  const pendingSlide = useRef<{ index: number; offset: number } | null>(null);
  const slideAnim = useRef<Animation | null>(null);
  // Read inside the gesture's window listeners, whose closures are fixed at pointerdown.
  const shownRef = useRef(shown);
  shownRef.current = shown;
  // The window listeners of the gesture in flight, so its end (or an unmount mid-drag) can remove them.
  const detach = useRef<(() => void) | null>(null);
  useEffect(() => () => detach.current?.(), []);

  const setArrangement = useCallback((next: string[] | null) => {
    orderRef.current = next;
    setOrder(next);
  }, []);

  // Play the displacement backwards on the row that gave up its slot, so it slides instead of teleporting.
  useIsoLayoutEffect(() => {
    const slide = pendingSlide.current;
    pendingSlide.current = null;
    if (!slide || !listRef.current) return;
    const displaced = listRef.current.querySelectorAll<HTMLElement>(`[${WORKSPACE_ROW_ATTR}]`)[slide.index];
    // jsdom has no Web Animations API, and a browser that lacks it simply keeps the old instant swap.
    if (!displaced || typeof displaced.animate !== 'function') return;
    slideAnim.current?.cancel();
    slideAnim.current = displaced.animate(
      [{ transform: `translateY(${slide.offset}px)` }, { transform: 'translateY(0px)' }],
      { duration: SLIDE_MS, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
  });

  const ordered = useMemo(() => {
    if (!order) return rows;
    const byId = new Map(rows.map((row) => [row.ws.id, row]));
    const named = order.map((id) => byId.get(id)).filter((row): row is WorkspaceRowModel => Boolean(row));
    // Anything the drag didn't know about (a space Orion created mid-gesture) keeps its place at the end.
    const rest = rows.filter((row) => !order.includes(row.ws.id));
    return [...named, ...rest];
  }, [rows, order]);

  /** Hand the arrangement over: the cache write inside `reorderWorkspaces` becomes the new truth. */
  const commit = useCallback((ids: string[]) => {
    const persisted = reorderWorkspaces(ids);
    setArrangement(null);
    // A refusal reverts the shared cache and re-reads inside `reorderWorkspaces`, so the list corrects
    // itself; there is nothing for this hook to undo.
    void persisted.catch(() => undefined);
  }, [setArrangement]);

  const swap = useCallback((from: number, to: number) => {
    const source = orderRef.current ?? ordered.map((row) => row.ws.id);
    if (to < 0 || to >= source.length || from === to) return;
    const next = [...source];
    const [id] = next.splice(from, 1);
    next.splice(to, 0, id);
    moved.current = true;
    // The row that was in `to` ends up in `from`, having travelled a whole step the other way.
    pendingSlide.current = { index: from, offset: (to - from) * step.current };
    setArrangement(next);
  }, [ordered, setArrangement]);

  const follow = useCallback((id: string, clientY: number) => {
    if (draggingRef.current !== id || step.current <= 0) return;
    const ids = orderRef.current ?? ordered.map((row) => row.ws.id);
    const count = Math.min(ids.length, shownRef.current);
    let index = ids.indexOf(id);
    // Past half a row, swap with the neighbour and re-anchor by exactly that step — so the list shows the
    // outcome as the pointer travels, and the two movements cancel on the dragged row itself. A fast drag
    // can cross several rows between two events, so keep stepping until the row has caught up.
    for (;;) {
      const slot = nextSlot(index, clientY - origin.current, step.current, count);
      if (slot === index) break;
      swap(index, slot);
      origin.current += (slot - index) * step.current;
      index = slot;
    }
    // The slot the row has ARRIVED in answers the clamp. See `nextSlot`.
    setLift(clampLift(clientY - origin.current, step.current, index, count));
  }, [ordered, swap]);

  const finish = useCallback((keep: boolean) => {
    detach.current?.();
    detach.current = null;
    draggingRef.current = null;
    setDraggingId(null);
    setLift(0);
    const ids = orderRef.current;
    if (keep && moved.current && ids) commit(ids);
    else setArrangement(null);
    // The click that follows a reordering release is swallowed by the row's `onClickCapture`. If the
    // release landed off the row, that click never reaches it, so clear the flag once it has had its turn
    // — otherwise the next real click on a Space would be eaten.
    setTimeout(() => { moved.current = false; }, 0);
  }, [commit, setArrangement]);

  const gripProps = useCallback(
    (id: string) => ({
      onPointerDown: (event: PointerEvent<HTMLElement>) => {
        if (!canReorder) return;
        if (event.button !== 0) return;
        // Don't let the row's link start a native drag, and don't select text while dragging.
        event.preventDefault();
        event.stopPropagation();
        const rowEl = event.currentTarget.closest<HTMLElement>(`[${WORKSPACE_ROW_ATTR}]`);
        step.current = measureStep(rowEl);
        listRef.current = rowEl?.parentElement ?? null;
        origin.current = event.clientY;
        moved.current = false;
        draggingRef.current = id;
        setDraggingId(id);
        setLift(0);
        setArrangement(ordered.map((row) => row.ws.id));

        const pointerId = event.pointerId;
        const onMove = (e: globalThis.PointerEvent) => { if (e.pointerId === pointerId) follow(id, e.clientY); };
        const onUp = (e: globalThis.PointerEvent) => { if (e.pointerId === pointerId) finish(true); };
        const onCancel = (e: globalThis.PointerEvent) => { if (e.pointerId === pointerId) finish(false); };
        detach.current?.();
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        detach.current = () => {
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onCancel);
        };
      },
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        if (!canReorder) return;
        if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
        // Without this the menu scrolls out from under the row being moved. It is also the only way to
        // reorder without a pointer, so it is not optional polish.
        event.preventDefault();
        const ids = ordered.map((row) => row.ws.id);
        const index = ids.indexOf(id);
        const to = event.key === 'ArrowUp' ? index - 1 : index + 1;
        if (to < 0 || to >= Math.min(ids.length, shownRef.current)) return;
        const next = [...ids];
        next.splice(to, 0, next.splice(index, 1)[0]);
        commit(next);
      },
    }),
    [canReorder, commit, finish, follow, ordered, setArrangement],
  );

  return {
    rows: ordered,
    canReorder,
    isDragging: (id: string) => draggingId === id,
    liftStyle: (id: string) =>
      draggingId === id ? { transform: `translateY(${lift}px)`, position: 'relative', zIndex: 1 } : undefined,
    rowProps: () => ({
      [WORKSPACE_ROW_ATTR]: '',
      draggable: false as const,
      onClickCapture: (event: { preventDefault: () => void; stopPropagation: () => void }) => {
        // A drag ends in a click on the row, which is a link — swallow exactly that one, or reordering
        // would also navigate into whichever space was dragged.
        if (!moved.current) return;
        moved.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
    }),
    gripProps,
  };
}
